import api from './api';

// Interfaces basées sur les modèles backend
export interface Produit {
  id: number;
  nom: string;
  code: string;
  type: string;
  unite: string;
  qualite_requise: boolean;
  prix_unitaire: number | null;
  categorie: string;
  description: string;
  poids_moyen: number | null;
  created_at: string;
  updated_at: string;
}

export interface Magasin {
  id: number;
  nom: string;
  code: string;
  type_magasin: 'DEPOT' | 'MAGASIN' | 'ENTREPOT' | 'SILO' | 'HANGAR' | 'PLATEFORME';
  adresse: string;
  ville: string;
  region: string;
  pays: string;
  code_postal: string;
  latitude: number | null;
  longitude: number | null;
  capacite_maximale: number | null;
  surface: number | null;
  hauteur: number | null;
  equipements: string;
  installations: string;
  responsable_nom: string;
  responsable_telephone: string;
  responsable_email: string;
  statut: 'ACTIF' | 'INACTIF' | 'MAINTENANCE' | 'FERME';
  est_operational: boolean;
  horaires_ouverture: string;
  site: number;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface Stock {
  id: number;
  produit: number;
  magasin: number;
  quantite_entree: number;
  quantite_sortie: number;
  stock_actuel: number;
  stock_minimum?: number | null;
  stock_maximum?: number | null;
  date_mise_a_jour: string;
  created_at: string;
  updated_at: string;
  // Relations
  produit_details?: Produit;
  magasin_details?: Magasin;
}

export interface MouvementStock {
  id: number;
  reference: string;
  produit: number;
  pesee: number;
  magasin_source: number | null;
  magasin_destination: number | null;
  sens: 'ENTREE' | 'SORTIE';
  quantite: number;
  type_sortie: string | null;
  motif: string;
  observations?: string;
  date_mouvement: string;
  utilisateur: number;
  operateur?: number;
  created_at: string;
  updated_at: string;
  // Relations
  produit_details?: Produit;
  pesee_details?: any;
  magasin_source_details?: Magasin;
  magasin_destination_details?: Magasin;
  utilisateur_details?: any;
  // Champs du backend (noms simples)
  produit_nom?: string;
  pesee_reference?: string;
  magasin_source_nom?: string;
  magasin_destination_nom?: string;
  operateur_nom?: string;
  sens_display?: string;
  type_sortie_display?: string;
  date_mouvement_display?: string;
}

export interface Pesee {
  id: number;
  reference: string;
  date_heure: string;
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  statut: string;
  produit: number | null;
  site: number;
  created_at: string;
  updated_at: string;
  // Relations
  produit_details?: Produit;
}

export interface PeseeComplete {
  id: number;
  reference: string;
  date_heure: string;
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  statut: string;
  type_pesee: 'reception' | 'expedition' | 'transfert';
  produit?: Produit;
  produit_info?: {
    id: number;
    nom: string;
    conditionnement_info?: {
      id: number;
      nom: string;
      poids_tare?: number;
    };
  };
  conditionnement?: string;
  conditionnement_info?: {
    id: number;
    nom: string;
    poids?: number;
  };
  camion?: string;
  site: number;
  created_at: string;
  updated_at: string;
}

export interface MiseEnStockRequest {
  pesees: number[];
  magasin_destination?: number;
  magasin_source?: number;
  type_sortie?: 'MAGASIN' | 'DEPOT' | 'USINE' | 'CLIENT' | 'REBUS' | 'AUTRE';
  observations?: string;
}

// Services API
class StockService {
  // Récupérer tous les stocks avec détails
  async getStocks(): Promise<Stock[]> {
    try {
      const response = await api.get('/weighing/api/stocks/?include_details=true');
     
      // Vérifier que la réponse contient des données valides
      if (!response.data) {
        console.warn('Aucune donnée reçue pour les stocks');
        return [];
      }
      
      // Si c'est un objet avec une propriété results (pagination), retourner results
      if (response.data.results && Array.isArray(response.data.results)) {
    
        return response.data.results;
      }
      
      // Si c'est directement un tableau, le retourner
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération des stocks:', error);
      if (error.response?.status === 401) {
        throw new Error('Authentification requise. Veuillez vous connecter.');
      }
      throw error;
    }
  }

  // Récupérer les stocks par magasin
  async getStocksByMagasin(magasinId: number): Promise<Stock[]> {
    try {
      const response = await api.get(`/weighing/api/stocks/?magasin=${magasinId}&include_details=true`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des stocks par magasin:', error);
      throw error;
    }
  }

  // Récupérer tous les magasins
  async getMagasins(): Promise<Magasin[]> {
    try {
      const response = await api.get('/weighing/api/magasins/'); 
      
      if (!response.data) {
        return [];
      }
      
      if (response.data.results && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des magasins:', error);
      throw error;
    }
  }

  // Récupérer tous les produits
  async getProduits(): Promise<Produit[]> {
    try {
      const response = await api.get('/weighing/api/produits/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  }

  // Récupérer les mouvements de stock
  async getMouvementsStock(params?: {
    date_debut?: string;
    date_fin?: string;
    produit?: number;
    magasin?: number;
    sens?: 'ENTREE' | 'SORTIE';
  }): Promise<MouvementStock[]> {
    try {
      const response = await api.get('/weighing/api/mouvements-stock/', { params });
      
      if (!response.data) {
        console.warn('Aucune donnée reçue pour les mouvements');
        return [];
      }
      
      if (response.data.results && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des mouvements de stock:', error);
      throw error;
    }
  }


  // Créer un mouvement de stock
  async createMouvementStock(mouvement: Partial<MouvementStock>): Promise<MouvementStock> {
    try {
      const response = await api.post('/weighing/api/mouvements-stock/', mouvement);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du mouvement de stock:', error);
      throw error;
    }
  }

  // Mettre à jour un stock
  async updateStock(stockId: number, stock: Partial<Stock>): Promise<Stock> {
    try {
      const response = await api.patch(`/weighing/api/stocks/${stockId}/`, stock);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du stock:', error);
      throw error;
    }
  }

  // Exporter les données en Excel
  async exportStocks(format: 'excel' | 'csv' = 'excel'): Promise<Blob> {
    try {
      const response = await api.get('/weighing/api/stocks/export/', {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'export des stocks:', error);
      throw error;
    }
  }

  // Exporter les mouvements quotidiens
  async exportMouvementsQuotidiens(dateDebut: string, dateFin: string): Promise<Blob> {
    try {
      const response = await api.get('/weighing/api/mouvements-stock/export-quotidiens/', {
        params: { date_debut: dateDebut, date_fin: dateFin },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'export des mouvements quotidiens:', error);
      throw error;
    }
  }

  // Exporter le rapport mensuel
  async exportRapportMensuel(annee: number): Promise<Blob> {
    try {
      const response = await api.get('/weighing/api/stocks/export-rapport-mensuel/', {
        params: { annee },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'export du rapport mensuel:', error);
      throw error;
    }
  }

  // Mettre en stock
  async mettreEnStock(request: MiseEnStockRequest): Promise<{ message: string }> {
    try {
      const response = await api.post('/weighing/api/pesees-completes/mettre-en-stock/', request);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise en stock:', error);
      throw error;
    }
  }

  // Récupérer les pesées complètes (prêtes pour la mise en stock)
  async getPeseesCompletes(params?: any): Promise<PeseeComplete[]> {
    try {
      // Utiliser l'endpoint spécifique pour les pesées complètes qui filtre déjà en_stock=False
      const response = await api.get('/weighing/api/pesees-completes/', { 
        params: { 
          ...params,
          include_details: true
        } 
      });
      
      if (!response.data) {
        console.warn('Aucune donnée reçue pour les pesées complètes');
        return [];
      }
      
      // Si c'est un objet avec une propriété results (pagination), retourner results
      if (response.data.results && Array.isArray(response.data.results)) {
        return response.data.results;
      }
      
      // Si c'est directement un tableau, le retourner
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      console.warn('Format de données inattendu pour les pesées complètes:', response.data);
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des pesées complètes:', error);
      throw error;
    }
  }

  // Méthodes pour les rapports avec toutes les relations
  async getMouvementsQuotidiens(dateDebut: string, dateFin: string, page: number = 1, pageSize: number = 10): Promise<any> {
    try {
      const response = await api.get('/weighing/api/stock-reports/mouvements-quotidiens/', {
        params: { date_debut: dateDebut, date_fin: dateFin, page, page_size: pageSize }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des mouvements quotidiens:', error);
      throw error;
    }
  }

  async getRapportMensuel(annee: number): Promise<any> {
    try {
      const response = await api.get('/weighing/api/stock-reports/rapport-mensuel/', {
        params: { annee: annee }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du rapport mensuel:', error);
      throw error;
    }
  }

  async getEnregistrementsDetailles(params: { date_debut: string; date_fin: string; page?: number; page_size?: number }): Promise<any> {
    try {
      const response = await api.get('/weighing/api/stock-reports/enregistrements-detailles/', {
        params: {
          ...params,
          page: params.page || 1,
          page_size: params.page_size || 20
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des enregistrements détaillés:', error);
      throw error;
    }
  }
}

const stockService = new StockService();
export default stockService;